/**
 * @file        theme.service.ts
 * @description Applies and persists Cypher v3 light and dark modes
 * @version     1.0.0
 * @author      Boris Veits
 * @copyright   Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *              Property of MindShare HDV LLC.  This file cannot be used or
 *              altered in anyway without expressed written permission from
 *              MindShare HDV LLC.
 */
import { Injectable } from '@angular/core';

import
{
    THEME_CHANGE_EVENT,
    THEME_CLASS,
    THEME_MODE,
    THEME_STORAGE_KEY
}
    from '@app/constants/Theme.constant';
import { ThemeMode } from
    '@app/secure/interface/ThemeMode.interface';

@Injectable
({
    providedIn: 'root'
})
class ThemeService
{
    private mode: ThemeMode;

    /**
     * @desc   - restore the saved color mode on startup
     * @access - public
     * @param  -
     * @return -
     */
    constructor()
    {
        this.mode = this.readStoredMode();
        this.applyMode(this.mode);
    }

    /**
     * @desc   - read a valid mode from localStorage
     * @access - private
     * @param  -
     * @return - stored mode or light
     */
    private readStoredMode(): ThemeMode
    {
        let stored;
        let result: ThemeMode;

        stored = localStorage.getItem(THEME_STORAGE_KEY);
        if (stored === THEME_MODE.DARK)
        {
            result = THEME_MODE.DARK;
        }
        else
        {
            result = THEME_MODE.LIGHT;
        }

        return result;
    }

    /**
     * @desc   - set html data-theme and Material theme class
     * @access - private
     * @param  - mode : light or dark
     * @return -
     */
    private applyMode
    (
        mode: ThemeMode
    ): void
    {
        let root;
        let changeEvent;

        root = document.documentElement;
        root.setAttribute('data-theme', mode);
        root.classList.remove(THEME_CLASS.LIGHT, THEME_CLASS.DARK);
        if (mode === THEME_MODE.DARK)
        {
            root.classList.add(THEME_CLASS.DARK);
        }
        else
        {
            root.classList.add(THEME_CLASS.LIGHT);
        }

        changeEvent = new CustomEvent(THEME_CHANGE_EVENT,
        {
            detail: mode
        });
        root.dispatchEvent(changeEvent);

        return;
    }

    /**
     * @desc   - return the active light or dark mode
     * @access - public
     * @param  -
     * @return - current theme mode
     */
    public getMode(): ThemeMode
    {
        let result;

        result = this.mode;

        return result;
    }

    /**
     * @desc   - whether dark mode is active
     * @access - public
     * @param  -
     * @return - true when dark mode is on
     */
    public isDark(): boolean
    {
        let result;

        result = this.mode === THEME_MODE.DARK;

        return result;
    }

    /**
     * @desc   - persist and apply a color mode
     * @access - public
     * @param  - mode : light or dark
     * @return -
     */
    public setMode
    (
        mode: ThemeMode
    ): void
    {
        this.mode = mode;
        localStorage.setItem(THEME_STORAGE_KEY, mode);
        this.applyMode(mode);

        return;
    }

    /**
     * @desc   - switch between light and dark modes
     * @access - public
     * @param  -
     * @return -
     */
    public toggle(): void
    {
        let next: ThemeMode;

        if (this.mode === THEME_MODE.DARK)
        {
            next = THEME_MODE.LIGHT;
        }
        else
        {
            next = THEME_MODE.DARK;
        }

        this.setMode(next);

        return;
    }

    /**
     * @desc   - tooltip for the header theme control
     * @access - public
     * @param  -
     * @return - action label for the other mode
     */
    public tooltip(): string
    {
        let result;

        if (this.isDark())
        {
            result = 'Switch to light mode';
        }
        else
        {
            result = 'Switch to dark mode';
        }

        return result;
    }

    /**
     * @desc   - re-apply the stored mode to the document
     * @access - public
     * @param  -
     * @return -
     */
    public applyStored(): void
    {
        this.applyMode(this.mode);

        return;
    }
}

export { ThemeService };
